// totpUtils.js
const Base32 = {
  alphabet: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567',

  encode(buffer) {
    let bits = 0
    let value = 0
    let output = ''

    for (let i = 0; i < buffer.length; i++) {
      value = (value << 8) | buffer[i]
      bits += 8
      while (bits >= 5) {
        output += this.alphabet[(value >>> (bits - 5)) & 31]
        bits -= 5
      }
    }
    if (bits > 0) output += this.alphabet[(value << (5 - bits)) & 31]

    return output
  },

  decode(str) {
    const cleaned = str.toUpperCase().replace(/[^A-Z2-7]/g, '')
    let bits = 0
    let value = 0
    const output = []

    for (let i = 0; i < cleaned.length; i++) {
      const idx = this.alphabet.indexOf(cleaned[i])
      if (idx === -1) continue

      value = (value << 5) | idx
      bits += 5

      if (bits >= 8) {
        output.push((value >>> (bits - 8)) & 0xff)
        bits -= 8
      }
    }
    return new Uint8Array(output)
  }
}

// Browser HMAC-SHA1
async function hmacSha1Browser(keyBytes, messageBytes) {
  const cryptoKey = await window.crypto.subtle.importKey(
    'raw',
    keyBytes,
    { name: 'HMAC', hash: 'SHA-1' },
    false,
    ['sign']
  )
  const signature = await window.crypto.subtle.sign(
    'HMAC',
    cryptoKey,
    messageBytes
  )
  return new Uint8Array(signature)
}

function getRandomBytes(length) {
  const array = new Uint8Array(length)
  window.crypto.getRandomValues(array)
  return array
}

// TOTP Class
export class TOTP {
  constructor({ digits = 6, hashAlgorithm = 'sha1' } = {}) {
    this.digits = digits
    this.hashAlgorithm = hashAlgorithm
  }

  static generateSecret(length = 20) {
    const randomBytes = getRandomBytes(length)
    return Base32.encode(randomBytes)
  }

  async generateTOTPCode(secret, interval = 30, time = null) {
    const currentTime = time ?? Math.floor(Date.now() / 1000)
    const counter = Math.floor(currentTime / interval)
    return await this._generateOTP(secret, counter)
  }

  async verifyCode(secret, code, interval = 30, discrepancy = 1) {
    const currentTime = Math.floor(Date.now() / 1000)
    for (let i = -discrepancy; i <= discrepancy; i++) {
      const otp = await this._generateOTP(
        secret,
        Math.floor((currentTime + i * interval) / interval)
      )
      if (otp === code) return true
    }
    return false
  }

  generateQRCodeUri(appName, secret, issuer = 'LiveAlarm', interval = 30) {
    const uri = new URL(`otpauth://totp/${encodeURIComponent(appName)}`)
    uri.searchParams.set('secret', secret)
    uri.searchParams.set('issuer', issuer)
    uri.searchParams.set('algorithm', this.hashAlgorithm.toUpperCase())
    uri.searchParams.set('digits', this.digits.toString())
    uri.searchParams.set('period', interval.toString())
    return uri.toString()
  }

  // Internal OTP generation using HMAC-SHA1
  async _generateOTP(secret, counter) {
    const key = Base32.decode(secret)
    const counterBuffer = new ArrayBuffer(8)
    const view = new DataView(counterBuffer)
    view.setUint32(4, counter, false) // big-endian

    const hmac = await hmacSha1Browser(key, new Uint8Array(counterBuffer))

    // Dynamic truncation (RFC 4226)
    const offset = hmac[hmac.length - 1] & 0xf
    const code =
      ((hmac[offset] & 0x7f) << 24) |
      ((hmac[offset + 1] & 0xff) << 16) |
      ((hmac[offset + 2] & 0xff) << 8) |
      (hmac[offset + 3] & 0xff)

    const otp = code % 10 ** this.digits
    return otp.toString().padStart(this.digits, '0')
  }
}

// Utility wrappers
const totp = new TOTP()
export function generateSecret() {
  return TOTP.generateSecret()
}
export function generateOTPAuthUrl(secret, email, issuer) {
  return totp.generateQRCodeUri(`${issuer}:${email}`, secret)
}
export function generateTOTP(secret) {
  return totp.generateTOTPCode(secret)
}
// Recovery codes are a full login bypass -- verifyTotp accepts one in place of
// an authenticator code -- so they must be unguessable.
//
// Both enrolment screens used `Math.random().toString(36).substring(2, 10)`.
// Math.random is NOT cryptographic: V8 runs xorshift128+, its internal state is
// recoverable from a handful of outputs, and all ten codes come from the same
// stream — so one leaked code narrows the other nine. The length was fine; the
// source was not.
//
// getRandomBytes above is already crypto.getRandomValues; this uses it and
// matches the mobile app's OtpUtils.generateBackupCodes -- 10 codes, 8
// characters, from a secure source.
export function generateRecoveryCodes(count = 10, length = 8) {
  const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  return Array.from({ length: count }, () => {
    const bytes = getRandomBytes(length)
    let code = ''
    // Rejection-free and unbiased enough: 256 % 36 != 0 skews the first four
    // letters by ~0.5%, which costs a fraction of a bit against a 36^8 space.
    for (let i = 0; i < length; i++) code += ALPHABET[bytes[i] % ALPHABET.length]
    return { code, isUsed: false }
  })
}

export function verifyTOTP(secret, code) {
  return totp.verifyCode(secret, code)
}
